import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  type TypographyProps,
} from "@mui/material";
import { usePlainAutocomplete } from "./logics";

export interface IPlainAutocompleteProps<T extends object> {
  value: T[];
  options: T[];
  onChange: (newValue: T[]) => void;
  placeholder?: string;
  groupBy?: (option: T) => string;
  getOptionLabel: (option: T) => string;
  getOptionKey?: (option: T) => string | number;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  groupLabelVariant?: TypographyProps["variant"];
  groupLabelColor?: string;
  showSelectAll?: boolean;
  showGroupCheckboxes?: boolean;
  showAllOptions?: boolean;
}

export const PlainAutocomplete = <T extends object>({
  value,
  options,
  onChange,
  placeholder = "Recherchez dans la liste...",
  groupBy,
  getOptionLabel,
  getOptionKey,
  isOptionEqualToValue,
  groupLabelVariant = "body1SemiBold",
  groupLabelColor,
  showSelectAll = true,
  showGroupCheckboxes = true,
  showAllOptions = false,
}: IPlainAutocompleteProps<T>) => {
  const {
    filteredOptions,
    inputValue,
    setInputValue,
    openGroups,
    setOpenGroups,
    groupedOptions,
    hasGroups,
    isAllSelected,
    isDesktop,
    isGroupSelected,
    isGroupIndeterminate,
    isOptionSelected,
    handleSelectAll,
    handleSelectGroup,
    handleSelectOption,
    totalOptionsCount,
    displayAllOptions,
    setDisplayAllOptions,
    getOptionLabel: resolvedGetOptionLabel,
    getOptionKey: resolvedGetOptionKey,
    getInputProps,
  } = usePlainAutocomplete({
    value,
    options,
    onChange,
    groupBy,
    getOptionLabel,
    getOptionKey,
    isOptionEqualToValue,
  });

  const { ref: inputRef, ...inputProps } = getInputProps();

  return (
    <Stack>
      <TextField
        size="small"
        fullWidth
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        inputRef={inputRef}
        slotProps={{
          htmlInput: inputProps,
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "secondary.main" }} />
              </InputAdornment>
            ),
            endAdornment: inputValue ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setInputValue("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
      />
      <Box sx={{ pt: 0.5 }}>
        {showSelectAll && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                sx={{ p: 0.5 }}
                checked={isAllSelected}
                indeterminate={!isAllSelected && filteredOptions.some((o) => isOptionSelected(o))}
                onChange={handleSelectAll}
              />
            }
            label={<Typography>Sélectionner tout</Typography>}
          />
        )}
        {groupedOptions.map((groupData, index) => {
          if (!isDesktop && totalOptionsCount > 20 && !displayAllOptions && index > 2 && !showAllOptions) {
            return null;
          }
          const { group, options: groupOptions, key } = groupData;
          return (
            <Box key={key}>
              {hasGroups && group !== "" && (
                <Box
                  sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                  onClick={() => setOpenGroups((prev) => ({ ...prev, [group]: !prev[group] }))}
                >
                  {showGroupCheckboxes ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          sx={{ p: 0.5 }}
                          checked={isGroupSelected(groupOptions)}
                          indeterminate={isGroupIndeterminate(groupOptions)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectGroup(groupOptions);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      }
                      label={
                        <Typography variant={groupLabelVariant} color={groupLabelColor}>
                          {group}
                        </Typography>
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <Box sx={{ flex: 1, px: 0.5 }}>
                      <Typography variant={groupLabelVariant} color={groupLabelColor}>
                        {group}
                      </Typography>
                    </Box>
                  )}
                  <IconButton size="small">
                    {openGroups[group] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Box>
              )}
              <Collapse in={!hasGroups || group === "" || openGroups[group]}>
                <Box sx={{ pl: hasGroups && group !== "" ? 2 : 0, display: "flex", flexDirection: "column" }}>
                  {groupOptions.map((opt) => (
                    <FormControlLabel
                      key={resolvedGetOptionKey(opt)}
                      control={
                        <Checkbox
                          size="small"
                          sx={{ paddingRight: 1, paddingLeft: 1, paddingTop: 0.5, paddingBottom: 0.5 }}
                          checked={isOptionSelected(opt)}
                          onChange={() => handleSelectOption(opt)}
                        />
                      }
                      label={<Typography variant="body1">{resolvedGetOptionLabel(opt)}</Typography>}
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
        {!isDesktop && totalOptionsCount > 20 && !showAllOptions && (
          <Button
            size="small"
            variant="text"
            onClick={() => setDisplayAllOptions(!displayAllOptions)}
            endIcon={displayAllOptions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: "none", width: "50%", textAlign: "left" }}
          >
            <Typography variant="body1SemiBold">{displayAllOptions ? "Masquer" : "Voir tout"}</Typography>
          </Button>
        )}
      </Box>
    </Stack>
  );
};
